import { Clock, Hash, History, Trash2 } from 'lucide-react';

import { NeedsRepairBadge } from '@/_shared/components/AccountRepair/NeedsRepairBadge';
import { KebabMenu } from '@/_shared/components/list/KebabMenu';
import { ListCard } from '@/_shared/components/list/ListCard';
import { MetaChip } from '@/_shared/components/list/MetaChip';
import { timeAgo } from '@/_shared/lib/format';

import {
  describeLastRun,
  describeTrigger,
  slackChannelId,
  slackChannelLabel,
  type WorkflowListItem,
} from '../lib/workflowList';
import { WorkflowStatusBadge } from './WorkflowStatusBadge';

interface WorkflowCardProps {
  workflow: WorkflowListItem;
  /** Destination href for the whole card (built with `wp()`). */
  to: string;
  isAdmin: boolean;
  onDelete: (id: string) => void;
  /** Slack channel id → "#name"; falls back to the id, then a plain "Slack" label. */
  resolveChannel: (channelId: string | null) => string | null;
}

/** A workflow as a card, for the list page's card view. */
export function WorkflowCard({
  workflow,
  to,
  isAdmin,
  onDelete,
  resolveChannel,
}: WorkflowCardProps): React.ReactElement {
  const channel = resolveChannel(slackChannelId(workflow.graph));
  const lastRun = describeLastRun(workflow.lastRunAt, workflow.lastRunStatus);
  const updated = workflow.updatedAt ? timeAgo(workflow.updatedAt) : null;

  return (
    <ListCard
      to={to}
      title={workflow.name}
      badges={
        <>
          <WorkflowStatusBadge status={workflow.status} />
          <NeedsRepairBadge listRow state={workflow.accountRepair?.state ?? 'ok'} className="shrink-0" />
        </>
      }
      actions={
        isAdmin && (
          <KebabMenu
            label={`Actions for ${workflow.name}`}
            actions={[{ label: 'Delete', icon: Trash2, destructive: true, onSelect: () => onDelete(workflow.id) }]}
          />
        )
      }
      description={workflow.description}
      meta={
        <>
          <MetaChip icon={Clock}>{describeTrigger(workflow.trigger)}</MetaChip>
          <MetaChip icon={Hash}>{slackChannelLabel(channel)}</MetaChip>
        </>
      }
      footerLeft={
        <span className={`inline-flex min-w-0 items-center gap-1 ${lastRun.tone}`}>
          <History aria-hidden className="size-3.5 shrink-0" />
          <span className="truncate">{lastRun.text}</span>
        </span>
      }
      footerRight={updated ? `Updated ${updated}` : null}
    />
  );
}
